import { useState, type ReactNode } from 'react';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@voll-intelligence/ui-kit/ui/sheet';
import { fmtTime, fmtWeekdayDate } from '../../../../shared/ui/flight_format.js';
import { formatCurrency } from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useGenUiI18n, type GenUiTranslate } from '../../../../shared/ui/i18n.js';
import type { CheckoutItem, MobilityItemDetail } from '@voll-intelligence/genui-contract';
import { CheckoutTripLegs, RULE_LINK } from '../../ui/checkout_trip_leg.js';
import { packageLabel } from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useCheckoutEnv } from '../../core/checkout_env.js';
import { maskPhone } from '../../../../mobility/shared/phone/phone_mask.js';
import { RIDE_ART } from '../../../../mobility/shared/ride_art/ride_art.js';
import { destinationOf, RideLeg } from './ride_leg.js';

export type MobilityCheckoutItem = Extract<CheckoutItem, { productType: 'mobility' }>;
type MobilityLeg = MobilityItemDetail['outward'];

const MUTED = 'text-xs leading-4 text-muted-foreground';

function serviceTitle(detail: MobilityItemDetail, t: GenUiTranslate): string {
  return detail.service === 'cooperative'
    ? t('productCheckout.mobility.cooperative')
    : t('mobility.sectionCarDriver');
}

/** O que a régua diz à esquerda: os acompanhantes da cooperativa, os extras do
 *  carro com motorista (bilíngue, pernoites). Vazio quando não há nada. */
function rulerText(detail: MobilityItemDetail, t: GenUiTranslate): string {
  if (detail.service === 'cooperative') {
    const n = detail.companions ?? 0;
    if (n === 0) return '';
    return t(
      n === 1
        ? 'productCheckout.mobility.companions_one'
        : 'productCheckout.mobility.companions_other',
      { count: n },
    );
  }
  const extras: string[] = [];
  if (detail.bilingual) extras.push(t('mobility.bilingual'));
  if (detail.overnightDays) {
    extras.push(
      t(detail.overnightDays === 1 ? 'mobility.overnight_one' : 'mobility.overnight_other', {
        count: detail.overnightDays,
      }),
    );
  }
  return extras.join(' · ');
}

function PackageColumn({ label }: { readonly label: string }) {
  const { t } = useGenUiI18n();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <p className={`m-0 ${MUTED} font-semibold text-foreground`}>
        {t('productCheckout.mobility.package')}
      </p>
      <p className="m-0 text-[13px] leading-[18px] font-medium text-foreground">{label}</p>
    </div>
  );
}

/** «Júlia Campos · +55 (11) 9 8765-4321»: o contato que o motorista usa. */
function contactText(contact: NonNullable<MobilityItemDetail['contact']>): string {
  const phone = maskPhone(contact.phone);
  return contact.name ? `${contact.name} · ${phone}` : phone;
}

function DetailRow({ label, value }: { readonly label: string; readonly value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2.5 text-[13px] leading-[18px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0 text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}

function LegRows({ leg, carDriver }: { readonly leg: MobilityLeg; readonly carDriver: boolean }) {
  const { t, locale } = useGenUiI18n();
  const destination = destinationOf(leg, carDriver);
  const stops = leg.stops ?? [];
  return (
    <dl className="m-0">
      <DetailRow
        label={t('productCheckout.mobility.pickupAt')}
        value={`${fmtWeekdayDate(leg.scheduleDate, locale)} · ${fmtTime(leg.scheduleDate)}`}
      />
      <DetailRow label={t('productCheckout.mobility.from')} value={leg.from} />
      {stops.length > 0 && (
        <DetailRow label={t('productCheckout.mobility.stops')} value={stops.join(' · ')} />
      )}
      {destination && <DetailRow label={t('productCheckout.mobility.to')} value={destination} />}
    </dl>
  );
}

const SECTION = 'mt-2 mb-0 text-xs font-semibold tracking-wider text-muted-foreground uppercase';

/** A ficha de «Detalhes»: só consulta — vale também com o card congelado
 *  depois do pedido. */
function MobilityDetailsSheet({
  item,
  open,
  onOpenChange,
}: {
  readonly item: MobilityCheckoutItem;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { t, locale } = useGenUiI18n();
  const { mobility } = useCheckoutEnv().copy;
  const { detail, price } = item;
  const carDriver = detail.service === 'car_driver';
  const pkg = packageLabel(detail, mobility);
  const extras = rulerText(detail, t);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent closeLabel={t('common.close')}>
        <SheetHeader>
          <SheetTitle>{t('productCheckout.mobility.detailsTitle')}</SheetTitle>
          <SheetDescription>
            {serviceTitle(detail, t)} · {detail.supplierName}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="gap-1">
          <h3 className={SECTION}>
            {t(carDriver ? 'productCheckout.mobility.start' : 'productCheckout.mobility.outward')}
          </h3>
          <LegRows leg={detail.outward} carDriver={carDriver} />
          {detail.return && (
            <>
              <h3 className={SECTION}>{t('productCheckout.mobility.return')}</h3>
              <LegRows leg={detail.return} carDriver={false} />
            </>
          )}
          <h3 className={SECTION}>{t('productCheckout.mobility.ride')}</h3>
          <dl className="m-0">
            {pkg && <DetailRow label={t('productCheckout.mobility.package')} value={pkg} />}
            {detail.flightNote && (
              <DetailRow label={t('productCheckout.mobility.notes')} value={detail.flightNote} />
            )}
            {detail.contact && (
              <DetailRow
                label={t('productCheckout.mobility.contact')}
                value={contactText(detail.contact)}
              />
            )}
            {extras && (
              <DetailRow
                label={t(
                  carDriver
                    ? 'productCheckout.mobility.extras'
                    : 'productCheckout.mobility.companionsLabel',
                )}
                value={extras}
              />
            )}
            {!detail.withinPolicy && (
              <DetailRow
                label={t('productCheckout.mobility.policy')}
                value={<span className="text-destructive">{t('mobility.outPolicy')}</span>}
              />
            )}
            <DetailRow
              label={t('productCheckout.mobility.totalEstimated')}
              value={formatCurrency(price.amount, price.currency, locale)}
            />
          </dl>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/** As linhas da corrida abaixo dos trechos (#6699): as observações que o motorista lê
 *  (o `flightNote`, que já junta o voo e o recado) e o contato do passageiro. */
function RideNotes({ detail }: { readonly detail: MobilityItemDetail }) {
  const { t } = useGenUiI18n();
  const { mobility } = useCheckoutEnv().copy;
  const rows: { label: string; value: string }[] = [];
  if (detail.flightNote)
    rows.push({ label: t('productCheckout.mobility.notes'), value: detail.flightNote });
  if (detail.contact) {
    rows.push({ label: t('productCheckout.mobility.contact'), value: contactText(detail.contact) });
  }
  return (
    <>
      {rows.length > 0 && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-caption">
          {rows.map((row) => (
            <div key={row.label} className="contents">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="m-0 min-w-0 break-words text-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {detail.excludesStops ? (
        <p className="m-0 text-caption text-muted-foreground">{mobility.excludesStops}</p>
      ) : null}
    </>
  );
}

/** O cabeçalho do card no celular (#6699): o carro 3D `Size=Small` (63×36,
 *  decorativo), o fornecedor e o serviço. No largo o carro maior fica ao lado
 *  dos trechos e este cabeçalho some. */
function NarrowHead({ detail }: { readonly detail: MobilityItemDetail }) {
  const { t } = useGenUiI18n();
  const art = RIDE_ART[detail.service];
  return (
    <div className="hidden items-center gap-3 @max-[theme(--breakpoint-sm)]/checkout:flex">
      <img
        src={art.src}
        srcSet={art.srcSet}
        width={63}
        height={36}
        alt=""
        draggable={false}
        className="h-9 w-[63px] flex-none object-contain"
      />
      {/* Nome longo quebra inteiro, sem reticências (#6321). */}
      <div className="flex min-w-0 flex-col break-words">
        <span className="text-ui font-medium text-foreground">{detail.supplierName}</span>
        <span className="text-detail text-muted-foreground">{serviceTitle(detail, t)}</span>
      </div>
    </div>
  );
}

/**
 * O bloco de mobilidade do card 1click (#5330, #6699): no largo, o carro da categoria
 * (`RIDE_ART`, 80×46, #6324) à esquerda dos trechos; no estreito, o cabeçalho com o carro
 * pequeno, o fornecedor e o serviço. A ida e a volta empilhadas (só com volta o trecho diz
 * «· Ida/Volta»), ou a partida e o pacote lado a lado no carro com motorista. Abaixo, as
 * observações e o contato do passageiro, e a régua com acompanhantes/extras e «Detalhes».
 * O total previsto e o escudo de fora da política moram no rodapé do card.
 */
export function MobilityCheckoutBlock({ item }: { readonly item: MobilityCheckoutItem }) {
  const { t } = useGenUiI18n();
  const { mobility } = useCheckoutEnv().copy;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { detail } = item;
  const carDriver = detail.service === 'car_driver';
  const ruler = rulerText(detail, t);
  // Sem nenhum campo de pacote, a coluna não existe — e não conta para a grade.
  const pkg = carDriver ? packageLabel(detail, mobility) : '';
  const outwardLabel = carDriver
    ? t('productCheckout.mobility.start')
    : detail.return
      ? t('productCheckout.mobility.outward')
      : undefined;
  const art = RIDE_ART[detail.service];
  return (
    <div className="flex flex-col gap-3">
      <NarrowHead detail={detail} />
      <div className="flex items-start gap-4">
        {/* O carro da categoria (#6324), o mesmo da linha de opção: decorativo. No card
            estreito ele sobe, menor, para o cabeçalho. */}
        <img
          src={art.src}
          srcSet={art.srcSet}
          width={80}
          height={46}
          alt=""
          draggable={false}
          className="h-[46px] w-20 flex-none object-contain @max-[theme(--breakpoint-sm)]/checkout:hidden"
        />
        {/* A mesma grade das pernas de voo e ônibus: lado a lado só a partida e o
            pacote; a ida e a volta empilham (#6699). */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <CheckoutTripLegs pair={Boolean(pkg)}>
            <RideLeg leg={detail.outward} label={outwardLabel} carDriver={carDriver} />
            {pkg && <PackageColumn label={pkg} />}
          </CheckoutTripLegs>
          {detail.return && (
            <RideLeg
              leg={detail.return}
              label={t('productCheckout.mobility.return')}
              carDriver={false}
            />
          )}
          <RideNotes detail={detail} />
        </div>
      </div>
      <div className="flex items-center gap-3">
        {ruler && <span className="text-xs leading-4 font-medium text-foreground">{ruler}</span>}
        {/* O link da régua do Trecho do voo (#6324): 12/16 Medium muted sublinhado. */}
        <button
          type="button"
          className={`ml-auto ${RULE_LINK}`}
          onClick={() => {
            setDetailsOpen(true);
          }}
        >
          {t('productCheckout.mobility.details')}
        </button>
      </div>
      <MobilityDetailsSheet item={item} open={detailsOpen} onOpenChange={setDetailsOpen} />
    </div>
  );
}
